/**
 * Every other move in the ink style (C0-VFX, the user's pick 2026-10-10: ink-brush trails, blood on
 * hits, kind colours for wind-ups and tells, no seal stamps). A move gets a template from its kind and
 * its effect category (render/skillFx.ts `skillLook`): a cut, a thrust, a palm burst, a qi blast, an
 * area slam, a claw rake, a break chop, a heal lotus, a buff, a power cut — drawn in the kind's
 * tone (attack vermilion, break amber, power gold, heal jade, buff sky). Each template's timeline
 * has the blow at `hit` seconds, so the game lines it up with the motion's contact.
 */
import { artOfSkill, type SkillDef } from '@nine/shared';
import { drawBeam, drawFlying, drawGiant, drawRain } from './swords';
import { arc, drawCut, drawSplats, inkSample, memo, P, slashStroke, splats, stroke, type BloodHit, type InkLayer, type InkScene } from './samples';
import type { Pt, Stroke } from './brush';
import { easeOut, hash2, span } from './noise';
import type { InkKind } from './palette';

export type TemplateId = 'cut' | 'rise' | 'thrust' | 'palm' | 'qi' | 'area' | 'claw' | 'break' | 'heal' | 'buff' | 'power' | SwordTemplate;
/** The sword spectacle templates (render/ink/swords.ts), for sword arts: beam, flown swords, sword rain, giant sword. */
export type SwordTemplate = 'beam' | 'flying' | 'rain' | 'giant';

export interface KindTemplate {
  id: TemplateId;
  /** When the blow lands in the template's timeline, how long it runs, the wind-up before a charged release. */
  hit: number;
  life: number;
  release: number;
  /** How a landed blow bleeds (null: no blood, a heal or a buff). */
  blood: BloodHit | null;
  draw(g: CanvasRenderingContext2D, t: number, sc: InkScene, layer: InkLayer): void;
}

const thrustStrokes = memo((D, tdy) => ({
  main: {
    pts: [
      [10, -46],
      [D * 0.5, tdy * 0.5 - 45],
      [D - 2, tdy - 44],
    ],
    w: 6,
    shape: 'flick',
    dry: 0.55,
    seed: 800,
  } as Stroke,
  streaks: [-7, 7].map(
    (d, i): Stroke => ({
      pts: [
        [14, -46 + d],
        [D - 14, tdy - 44 + d * 0.6],
      ],
      w: 2,
      shape: 'swell',
      dry: 0.7,
      body: 'acc',
      seed: 801 + i,
    }),
  ),
}), 'width');

const areaStrokes = memo((D, tdy) => {
  const cx = D;
  const cy = tdy;
  return {
    ring: { pts: arc(cx, cy, 34, 15, -2.2, -2.2 + Math.PI * 1.85, 18), w: 5, shape: 'press', dry: 0.6, seed: 810 } as Stroke,
    ring2: { pts: arc(cx, cy, 20, 9, 0.9, 0.9 - Math.PI * 1.7, 14), w: 3, shape: 'press', dry: 0.5, body: 'acc', seed: 811 } as Stroke,
    rays: Array.from({ length: 7 }, (_, i): Stroke => {
      const a = (i / 7) * Math.PI * 2 + 0.3;
      return {
        pts: [
          [cx + Math.cos(a) * 10, cy + Math.sin(a) * 4.5],
          [cx + Math.cos(a) * (26 + 8 * hash2(i, 1, 812)), cy + Math.sin(a) * (12 + 4 * hash2(i, 2, 812))],
        ],
        w: 3.5,
        shape: 'flick',
        dry: 0.6,
        seed: 813 + i,
      };
    }),
  };
}, 'box');
const AREA_SPRAY = splats(820, 14, -Math.PI / 2, 2.6, 120, 2.5, 0.4);

const clawStrokes = memo((D, tdy) => ({
  rakes: [-6, 0, 6].map(
    (d, i): Stroke => ({
      pts: [
        [D - 12 + d, tdy - 64],
        [D - 2 + d, tdy - 46],
        [D + 6 + d, tdy - 28],
      ],
      w: 3.5,
      shape: 'flick',
      dry: 0.4,
      seed: 830 + i,
    }),
  ),
}), 'box');

/** The templates, from the samples (played from where their blow falls) and four new ones. */
export const TEMPLATES: Readonly<Record<TemplateId, KindTemplate>> = {
  cut: { id: 'cut', hit: 0.08, life: 0.6, release: 0, blood: 'down', draw: (g, t, sc, L) => L === 'over' && drawCut(g, 1, t - 0.08, sc, 'trail') },
  rise: { id: 'rise', hit: 0.08, life: 0.6, release: 0, blood: 'up', draw: (g, t, sc, L) => L === 'over' && drawCut(g, 2, t - 0.08, sc, 'trail') },
  thrust: {
    id: 'thrust',
    hit: 0.07,
    life: 0.55,
    release: 0,
    blood: 'spit',
    draw(g, t, sc, L) {
      if (L !== 'over') return;
      const S = thrustStrokes(sc);
      slashStroke(g, S.main, 'attack', sc, easeOut(span(t, 0, 0.07)), span(t, 0.16, 0.45), span(t, 0.1, 0.35));
      for (const st of S.streaks) slashStroke(g, st, 'attack', sc, easeOut(span(t, 0.01, 0.08)), span(t, 0.12, 0.3), span(t, 0.08, 0.25));
    },
  },
  palm: { id: 'palm', hit: 0.06, life: 0.8, release: 0, blood: 'spit', draw: (g, t, sc, L) => inkSample('palm').draw(g, t + 0.28, { ...sc, blood: false }, L) },
  qi: { id: 'qi', hit: 0.22, life: 0.95, release: 0, blood: 'spit', draw: (g, t, sc, L) => inkSample('qi').draw(g, t + 0.3, { ...sc, blood: false }, L) },
  area: {
    id: 'area',
    hit: 0.06,
    life: 0.9,
    release: 0,
    blood: 'spit',
    draw(g, t, sc, L) {
      const S = areaStrokes(sc);
      if (L === 'ground') {
        stroke(g, S.ring, 'attack', sc, easeOut(span(t, 0, 0.14)), span(t, 0.4, 0.85));
        stroke(g, S.ring2, 'attack', sc, easeOut(span(t, 0.03, 0.16)), span(t, 0.35, 0.7));
        for (let i = 0; i < S.rays.length; i++) stroke(g, S.rays[i]!, 'attack', sc, easeOut(span(t, 0.02, 0.1)), span(t, 0.3, 0.65));
        return;
      }
      drawSplats(g, AREA_SPRAY, 'attack', sc, sc.D, sc.tdy - 4, t - 0.04, sc.tdy + 2, 0.4);
    },
  },
  claw: {
    id: 'claw',
    hit: 0.06,
    life: 0.6,
    release: 0,
    blood: 'down',
    draw(g, t, sc, L) {
      if (L !== 'over') return;
      const S = clawStrokes(sc);
      S.rakes.forEach((st, i) => slashStroke(g, st, 'attack', sc, easeOut(span(t, i * 0.012, 0.06 + i * 0.012)), span(t, 0.14, 0.42)));
    },
  },
  break: { id: 'break', hit: 0.22, life: 1.1, release: 0.5, blood: 'down', draw: (g, t, sc, L) => inkSample('break').draw(g, t + 0.5, { ...sc, blood: false }, L) },
  heal: { id: 'heal', hit: 2, life: 2, release: 0, blood: null, draw: (g, t, sc, L) => inkSample('heal').draw(g, t, sc, L) },
  buff: {
    id: 'buff',
    hit: 1.2,
    life: 1.2,
    release: 0,
    blood: null,
    // The heal's rising ink and petals, quicker, in the buff's sky.
    draw: (g, t, sc, L) => inkSample('heal').draw(g, 0.25 + t * 1.4, { ...sc, tone: 'buff' }, L),
  },
  power: {
    id: 'power',
    hit: 0.22,
    life: 1.1,
    release: 0,
    blood: 'big',
    // Only from just before its cut (the ultimate's own gathering is not this move's).
    draw: (g, t, sc, L) => t > -0.25 && inkSample('ult').draw(g, t + 1.3, { ...sc, blood: false, noGlyph: true }, L),
  },
  // ── sword spectacle (C0-VFX-PREP): sword arts' thrusts, ranged moves, areas and power moves ──
  beam: { id: 'beam', hit: 0.1, life: 0.6, release: 0, blood: 'spit', draw: (g, t, sc, L) => drawBeam(g, t, sc, L, sc.tone ?? 'attack', 0.1) },
  flying: { id: 'flying', hit: 0.32, life: 0.8, release: 0, blood: 'spit', draw: (g, t, sc, L) => drawFlying(g, t, sc, L, sc.tone ?? 'attack', 0.32) },
  rain: { id: 'rain', hit: 0.3, life: 1.1, release: 0, blood: 'spit', draw: (g, t, sc, L) => drawRain(g, t, sc, L, sc.tone ?? 'attack', 0.3) },
  giant: { id: 'giant', hit: 0.45, life: 1.5, release: 0, blood: 'big', draw: (g, t, sc, L) => drawGiant(g, t, sc, L, sc.tone ?? 'power', 0.45) },
};

/** The category of effect a move has (render/skillFx.ts `FxCategory`), as the templates need it. */
type Category = 'slash' | 'thrust' | 'qi' | 'palm' | 'area' | 'heal' | 'buff' | 'volley' | 'warp' | 'bite';

/** A move's template and tone, from its kind, its effect category and its motion. */
export function templateOf(s: SkillDef, category: Category, motion: string): { t: KindTemplate; tone: InkKind } {
  const tone: InkKind = s.kind;
  // Sword arts get the sword spectacle where it fits (the user, 2026-10-10: like the golden dragon palm).
  if (artOfSkill(s.id)?.weapon === 'sword' && s.kind !== 'heal' && s.kind !== 'buff' && s.kind !== 'break') {
    const sw: SwordTemplate | null =
      s.kind === 'power' ? 'giant' : category === 'thrust' ? 'beam' : category === 'qi' || category === 'volley' ? 'flying' : category === 'area' ? 'rain' : null;
    if (sw) return { t: TEMPLATES[sw], tone };
  }
  const id: TemplateId =
    s.kind === 'heal'
      ? 'heal'
      : s.kind === 'buff'
        ? 'buff'
        : s.kind === 'break'
          ? 'break'
          : s.kind === 'power'
            ? 'power'
            : category === 'thrust'
              ? 'thrust'
              : category === 'palm'
                ? 'palm'
                : category === 'qi' || category === 'volley'
                  ? 'qi'
                  : category === 'area'
                    ? 'area'
                    : category === 'bite'
                      ? 'claw'
                      : motion === 'rising'
                        ? 'rise'
                        : 'cut';
  return { t: TEMPLATES[id], tone };
}

/** A local point helper for the lab's labels. */
export const at = (sc: InkScene, x: number, y: number): Pt => P(sc, x, y);

// ── volleys and warps (C0-VFX round 4) ──

const dartStrokes = memo((D, tdy) => ({
  dart: {
    pts: [
      [12, -50],
      [D * 0.5, tdy * 0.5 - 56],
      [D - 4, tdy - 46],
    ],
    w: 4,
    shape: 'flick',
    dry: 0.4,
    seed: 900,
  } as Stroke,
  ring: { pts: arc(D - 2, tdy - 46, 7, 7, -2, -2 + Math.PI * 1.8, 10), w: 2.5, shape: 'press', dry: 0.4, seed: 901 } as Stroke,
}), { '*': 'width', ring: 'box' });

/**
 * One projectile of a volley (a flying sword, a needle, a qi bolt) as a brush dart, `tau` seconds
 * from its landing: the head runs to the target over the last 0.2 s, its tail dissolving, then a
 * small brush ring where it strikes.
 */
export function drawDart(g: CanvasRenderingContext2D, tau: number, sc: InkScene): void {
  if (tau < -0.22 || tau > 0.4) return;
  const S = dartStrokes(sc);
  stroke(g, S.dart, 'attack', sc, span(tau, -0.2, 0), span(tau, 0.02, 0.15), span(tau, -0.08, 0.06));
  stroke(g, S.ring, 'attack', sc, easeOut(span(tau, 0, 0.08)), span(tau, 0.14, 0.38));
}

const warpStrokes = memo((D, tdy) => ({
  dash: {
    pts: [
      [0, -40],
      [D * 0.5, tdy * 0.5 - 44],
      [D, tdy - 40],
    ],
    w: 7,
    shape: 'press',
    dry: 0.75,
    seed: 910,
  } as Stroke,
}), 'width');

/**
 * A warp of a warp chain, `t` seconds after it: a dry-brush dash stroke from where the fighter
 * vanished to where they reappear (`D`, `tdy` from the start), wiped from its start as it fades.
 */
export function drawWarp(g: CanvasRenderingContext2D, t: number, sc: InkScene): void {
  if (t < 0 || t > 0.5) return;
  slashStroke(g, warpStrokes(sc).dash, 'attack', sc, easeOut(span(t, 0, 0.06)), span(t, 0.2, 0.45), span(t, 0.06, 0.3));
}
